'use client'

import { ChainUtils, getChainAddresses } from '@morpho-org/blue-sdk'
import { type Client, erc20Abi, isAddressEqual } from 'viem'
import { getBalance, multicall } from 'viem/actions'

import {
  type AssetItem,
  getErc20Holdings,
  getNativeHolding,
  getStakedTokenHoldings,
  processAssetsForHoldings,
  type UserHolding
} from '@/helpers/holdings'

export interface FetchUserHoldingsParams {
  client: Client
  assets: AssetItem[]
  userAddress: `0x${string}`
  targetChainId: number
}

/**
 * Pure function that fetches all user token holdings across native, ERC20, and staked tokens.
 * @param params - Parameters object
 * @param params.client - Viem client instance
 * @param params.assets - Array of assets to check balances for
 * @param params.userAddress - User's wallet address
 * @param params.targetChainId - Target chain ID
 * @returns Array of user holdings with non-zero balances
 */
export async function fetchUserHoldings({
  client,
  assets,
  userAddress,
  targetChainId
}: FetchUserHoldingsParams): Promise<UserHolding[]> {
  const { allAssets, stakedTokenConfig } = processAssetsForHoldings(assets, targetChainId)

  const [nativeBal, erc20Results] = await Promise.all([
    ChainUtils.hasReliableNativeBalance(targetChainId)
      ? getBalance(client, { address: userAddress })
      : Promise.resolve(0n),
    multicall(client, {
      contracts: allAssets.map(a => ({
        abi: erc20Abi,
        address: a.address,
        functionName: 'balanceOf' as const,
        args: [userAddress]
      }))
    })
  ])

  const { wNative } = getChainAddresses(targetChainId)
  const wNativeToken = wNative ? assets.find(a => isAddressEqual(a.address, wNative)) : undefined

  const nativeHolding = getNativeHolding(nativeBal, wNativeToken)
  const erc20Holdings = getErc20Holdings(erc20Results, allAssets)
  const baseHoldings = [...nativeHolding, ...erc20Holdings]

  const stakedTokenHoldings = getStakedTokenHoldings(baseHoldings, stakedTokenConfig, assets)

  return [...baseHoldings, ...stakedTokenHoldings].filter(h => h.balance > 0n)
}
